import { useTranslation } from "react-i18next";

import { Bytes } from "@/components/bytes";
import { describeExpiry, StatCell, StatStrip } from "@/components/stat-strip";
import { StatusBadge } from "@/components/status-badge";
import { tsToDate } from "@/lib/format";
import type { FirestoreTimestamp, ZipStatusValue } from "@/lib/types";

interface Props {
  archived: boolean;
  zipStatus: ZipStatusValue;
  fileCount: number;
  bytes: number;
  expiresAt: FirestoreTimestamp | null;
}

/** At-a-glance health row for a site (admin site detail). */
export function SiteHealthStrip({ archived, zipStatus, fileCount, bytes, expiresAt }: Props) {
  const { t } = useTranslation();
  const expiry = describeExpiry(tsToDate(expiresAt));

  return (
    <StatStrip columns={4}>
      <StatCell label={t("siteHealth.status")}>
        {archived ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            {t("siteHealth.archived")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {t("siteHealth.active")}
          </span>
        )}
      </StatCell>

      <StatCell label={t("siteHealth.uploads")}>
        <span className="inline-flex items-baseline gap-1.5">
          <span>{t("common.files", { count: fileCount })}</span>
          <span className="text-xs text-muted-foreground">
            <Bytes value={bytes} />
          </span>
        </span>
      </StatCell>

      <StatCell label={t("siteHealth.zipArchive")}>
        <StatusBadge status={zipStatus} />
      </StatCell>

      <StatCell label={t("siteHealth.expires")}>
        <span className={expiry.className}>{expiry.label}</span>
      </StatCell>
    </StatStrip>
  );
}
